import React, { useState, useEffect, useRef } from "react";
import Loader from "../../components/Common/Loader";
import { Select } from "antd";
import { AutoComplete } from "antd";
import { useDispatch, useSelector } from "react-redux";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ToastNotification from "../../components/Common/ToastNotification";
import {
  fetchRecipesByNameThunk,
  clearUpdateMessage,
  resetRecipes,
} from "../../redux/Slices/recipesListSlice";
import { Col, Row } from "react-bootstrap";
import { CONSTANTS, mealTime } from "../../utils/Constant/Constant";
import RecipesCard from "../Client/RecipesCard";
import RecipeDrawer from "../Client/RecipeDetailsDrawer";
import { isValidNumberInput, isValidTextInput } from "../../utils/Utils";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import { fetchPreferencesAllergies } from "../../redux/Slices/preferencesAllergiesSlice";

const Recipes = ({ coachId }) => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  const {
    recipes,
    isloading,
    updateSuccessMessage,
    updateFailedMessage,
    hasMore,
    pageNumber,
    pageSize,
  } = useSelector((state) => state.getRecipes);
  const { recipeDetails } = useSelector((state) => state.getRecipesById);
  const { preferences, allergies } = useSelector(
    (state) => state.preferencesAllergies
  );

  const prevValueRef = useRef("");
  const [visible, setVisible] = useState(false);
  const [toShowFilter, setToShowFilter] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [recipesList, setRecipesList] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [allergiesState, setAllergiesState] = useState([]);
  const [calories, setCalories] = useState();
  const [protein, setProtein] = useState();
  const [fat, setFat] = useState();
  const [carbs, setCarbs] = useState();
  const [toastConfig, setToastConfig] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isToggled, setIsToggled] = useState(false);
  const [makeFavoriteMessage, setMakeFavoriteMessage] = useState("");
  const [macroError, setMacroError] = useState("");
  const [isPercentageValid, setIsPercentageValid] = useState(true);
  const [calorieError, setCalorieError] = useState("");

  // Dietary conflicts mapping
  const DIETARY_CONFLICTS_BY_NAME = {
    Pescatarian: ["Vegan"],
    Vegan: ["Perscatarian", "Pescatarian", "Halal", "Kosher", "Dairy-Free"],
    Halal: ["Vegan", "Kosher"],
    Kosher: ["Vegan", "Halal"],
    "Dairy-Free": ["Vegan"],
  };

  useEffect(() => {
    if (prevValueRef.current !== "" && filterSearch === "") {
      searchFilterHandle();
    }
    prevValueRef.current = filterSearch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSearch]);

  useEffect(() => {
    if (protein > 0 || fat > 0 || carbs > 0) {
      const total = Number(protein) + Number(fat) + Number(carbs);

      if (!calories || Number(calories) <= 0) {
        setCalorieError(
          trans("ClientMealPlan.dailyIntake") + " " + trans("Common.isRequired")
        );
      } else {
        setCalorieError("");
      }

      if (total === 100) {
        setMacroError("");
      } else if (total > 100) {
        setMacroError(trans("ClientMealPlan.proteinPercentageError"));
      } else {
        setMacroError(trans("ClientMealPlan.proteinPercentageError"));
      }

      setIsPercentageValid(
        total === 100 && (!calories || Number(calories) > 0)
      );
    } else {
      setMacroError("");
      setCalorieError("");
    }
  }, [protein, fat, carbs, calories, trans]);

  useEffect(() => {
    if (updateSuccessMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: updateSuccessMessage,
        heading: trans("Common.success"),
      });
      dispatch(clearUpdateMessage());
    } else if (updateFailedMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: updateFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearUpdateMessage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, updateSuccessMessage, updateFailedMessage]);

  useEffect(() => {
    if (makeFavoriteMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: makeFavoriteMessage,
        heading: trans("Common.success"),
      });
      setTimeout(() => {
        setToastConfig(null);
        setMakeFavoriteMessage("");
      }, 3000);
    }
  }, [makeFavoriteMessage, trans]);

  useEffect(() => {
    if (filterSearch.length >= 3) {
      // Reset to page 1 when searching
      dispatch(resetRecipes());
      const requestPayload = {
        carbs: "",
        fats: "",
        protein: "",
        receipeCount: 20,
        targetCalories: 0,
        preferences: "",
        allergies: "",
        recipeName: filterSearch || "",
        pagination: { pageNumber: 1, pageSize: pageSize },
        isCustomized: false,
      };
      dispatch(fetchRecipesByNameThunk({ requestPayload, mealTime: "" }));
    }
  }, [filterSearch, dispatch, pageSize]);

  // ===== Pagination Handlers =====
  const fetchData = (page, size) => {
    const requestPayload = {
      carbs: carbs > 0 ? carbs.toString() : "",
      fats: fat > 0 ? fat.toString() : "",
      protein: protein > 0 ? protein.toString() : "",
      receipeCount: size,
      targetCalories: calories > 0 ? parseInt(calories) : 0,
      preferences: dietaryPreferences.join(","),
      allergies: allergiesState.join(","),
      recipeName: filterSearch || "",
      pagination: { pageNumber: page, pageSize: size },
      isCustomized: false,
      selectedRecipeId: [],
    };

    const mealTimeVal =
      selectedCourse && selectedCourse.length > 0 ? selectedCourse : "";

    dispatch(
      fetchRecipesByNameThunk({ requestPayload, mealTime: mealTimeVal })
    );
  };

  useEffect(() => {
    dispatch(fetchPreferencesAllergies());
    dispatch(resetRecipes());
    fetchData(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextPage = () => {
    if (hasMore && !isloading) {
      fetchData(pageNumber + 1, pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1 && !isloading) {
      fetchData(pageNumber - 1, pageSize);
    }
  };

  const getDisabledPreferences = (selected, allOptions) => {
    const disabledSet = new Set();

    selected.forEach((prefId) => {
      const prefText = allOptions.find(
        (o) => o.optionId === prefId
      )?.optionText;
      if (prefText && DIETARY_CONFLICTS_BY_NAME[prefText]) {
        DIETARY_CONFLICTS_BY_NAME[prefText].forEach((conflict) =>
          disabledSet.add(conflict)
        );
      }
    });

    // Do not disable already-selected ones
    selected.forEach((prefId) => {
      const prefText = allOptions.find(
        (o) => o.optionId === prefId
      )?.optionText;
      disabledSet.delete(prefText);
    });

    return disabledSet;
  };

  const searchFilterHandle = (direction = "after") => {
    setToastConfig(null);

    // Block search if invalid
    if (
      (protein > 0 || fat > 0 || carbs > 0) &&
      (!calories || Number(calories) <= 0)
    ) {
      setCalorieError(
        trans("ClientMealPlan.dailyIntake") + " " + trans("Common.isRequired")
      );
      return;
    }

    // Reset pagination when applying new filters
    dispatch(resetRecipes());

    // Create the payload in the specified format
    const requestPayload = {
      carbs: carbs > 0 ? carbs.toString() : "",
      fats: fat > 0 ? fat.toString() : "",
      protein: protein > 0 ? protein.toString() : "",
      receipeCount: pageSize,
      targetCalories: calories > 0 ? parseInt(calories) : 0,
      preferences: dietaryPreferences.join(","),
      allergies: allergiesState.join(","),
      recipeName: filterSearch || "",
      pagination: { pageNumber: 1, pageSize: pageSize },
    };
    const mealTime =
      selectedCourse &&
      selectedCourse.length > 0 &&
      selectedCourse !== CONSTANTS.COURSE
        ? selectedCourse
        : "";

    if (isPercentageValid) {
      dispatch(fetchRecipesByNameThunk({ requestPayload, mealTime }));
    }

    setToastConfig(null);
  };

  const handleAllergiesChange = (value) => {
    setAllergiesState(value);
  };

  const openFilter = () => {
    setToShowFilter(!toShowFilter);
  };

  const handleClearFilter = () => {
    // Clear all filter states
    setFilterSearch("");
    setSelectedCourse([]);
    setDietaryPreferences([]);
    setAllergiesState([]);
    setCalories("");
    setProtein("");
    setFat("");
    setCarbs("");
    setMacroError("");
    setCalorieError("");
    setIsPercentageValid(true);

    // Reset pagination and fetch initial data
    dispatch(resetRecipes());

    // Create payload with empty filters
    const requestPayload = {
      carbs: "",
      fats: "",
      protein: "",
      receipeCount: pageSize,
      targetCalories: 0,
      preferences: "",
      allergies: "",
      recipeName: "",
      pagination: { pageNumber: 1, pageSize: pageSize },
      isCustomized: false,
    };

    // Fetch recipes with empty filters
    dispatch(fetchRecipesByNameThunk({ requestPayload, mealTime: "" }));

    // Close filter panel if open
    setToShowFilter(false);
  };

  useEffect(() => {
    setRecipesList(recipes);
  }, [recipes]);

  const closeDrawer = () => {
    setVisible(false);
  };

  const handleChangeCalories = (e) => {
    const { value } = e.target;
    if (isValidNumberInput(value)) {
      setCalories(value);
    }
  };

  const handleChangeProtein = (e) => {
    const { value } = e.target;
    if (isValidNumberInput(value)) {
      setProtein(value);
    }
  };

  const handleChangeFat = (e) => {
    const { value } = e.target;
    if (isValidNumberInput(value)) {
      setFat(value);
    }
  };

  const handleChangeCarbs = (e) => {
    const { value } = e.target;
    if (isValidNumberInput(value)) {
      setCarbs(value);
    }
  };

  const handleChangeInput = (value) => {
    // Allow letters and empty string
    if (isValidTextInput(value)) {
      setFilterSearch(value);
    }
  };

  return (
    <div>
      <div className="d-flex recipe-filter-search ">
        <AutoComplete
          value={filterSearch}
          onChange={handleChangeInput}
          placeholder="Search recipes by name"
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          allowClear
          options={[]}
          className="search-filter-recipes"
        />
        <button
          onClick={searchFilterHandle}
          className="search-button-recipe-filter"
        >
          Search
        </button>
        <div className="mx-auto filter-icon-recipes d-flex pr-4">
          <i onClick={openFilter} className="bi bi-funnel-fill"></i>
          <button
            onClick={handleClearFilter}
            className="search-button-recipe-filter pt-0 pb-0"
          >
            Clear
            <i
              className="bi bi-x-circle-fill clear-filter-icon pl-2"
              title="Clear Filter"
              style={{ cursor: "pointer", height: "20px" }}
            ></i>
          </button>
        </div>
      </div>

      <div className="">
        {toShowFilter && (
          <div className="recipe-filter-background recipe-filter">
            <div className="d-flex">
              <div></div>
            </div>
            <Row xs={12} sm={12} md={12} lg={12}>
              <Col xs={3} sm={3} md={3} lg={3}>
                <FormLabel label={trans("ClientMealPlan.meal")} />
                <Select
                  placeholder={trans("ClientMealPlan.meal")}
                  value={selectedCourse}
                  onChange={setSelectedCourse}
                  className="w-100"
                >
                  {mealTime?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={4} sm={4} md={4} lg={4}>
                <FormLabel label={trans("ClientMealPlan.preferences")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.preferences")
                  }
                  value={dietaryPreferences}
                  onChange={setDietaryPreferences}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {preferences?.options?.map((option) => {
                    const disabledSet = getDisabledPreferences(
                      dietaryPreferences,
                      preferences?.options || []
                    );
                    return (
                      <Select.Option
                        key={option.optionId}
                        value={option.optionId}
                        disabled={disabledSet.has(option.optionText)}
                      >
                        {option.optionText}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>

              <Col xs={4} sm={4} md={4} lg={4}>
                <FormLabel label={trans("ClientMealPlan.allergies")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.allergies")
                  }
                  value={allergiesState}
                  onChange={handleAllergiesChange}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {allergies?.options?.map((option) => (
                    <Select.Option
                      key={option.optionId}
                      value={option.optionId}
                    >
                      {option.optionText}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row xs={12} sm={12} md={12} lg={12} className="mt-3">
              <Col xs={2} sm={2} md={2} lg={2}>
                <FormLabel
                  label={trans("ClientMealPlan.mealIntake")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.mealIntake")
                  }
                  name="dailyIntake"
                  value={calories}
                  onChange={handleChangeCalories}
                  className="rounded-3"
                  suffix="Cal"
                  isInvalid={!!calorieError}
                  feedback={calorieError}
                />
              </Col>

              <Col xs={2} sm={2} md={2} lg={2}>
                <FormLabel
                  label={trans("ClientMealPlan.protein")}
                  className="text-black"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.protein")
                  }
                  name="proteinPercentage"
                  value={protein}
                  onChange={handleChangeProtein}
                  className="rounded-3"
                  suffix="%"
                />
              </Col>
              <Col xs={2} sm={2} md={2} lg={2}>
                <FormLabel
                  label={trans("ClientMealPlan.carbs")}
                  className="text-black"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.carbs")
                  }
                  name="carbsPercentage"
                  value={carbs}
                  onChange={handleChangeCarbs}
                  className="rounded-3"
                  suffix="%"
                />
              </Col>
              <Col xs={2} sm={2} md={2} lg={2}>
                <FormLabel
                  label={trans("ClientMealPlan.fat")}
                  className="text-gray-700"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.fat")
                  }
                  name="fatPercentage"
                  value={fat}
                  onChange={handleChangeFat}
                  className="rounded-3"
                  suffix="%"
                />
              </Col>
            </Row>

            {macroError && (
              <Row className="mt-2">
                <Col xs={12}>
                  <div className="text-danger">{macroError}</div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </div>
      {isloading ? (
        <div>
          <Loader />
        </div>
      ) : (
        <div className="recipe-list">
          {recipesList?.length > 0 ? (
            recipesList?.map((node) => (
              <div className="mx-3 m-2 mt-3" key={node.id}>
                <RecipesCard
                  isToolbox={true}
                  recipe={node}
                  key={node.id}
                  isToggled={isToggled}
                  setMakeFavoriteMessage={setMakeFavoriteMessage}
                />
              </div>
            ))
          ) : (
            <div className="no-recipe-found">
              {trans("recipes.noRecipeFound")}
            </div>
          )}
          {/* Pagination Controls */}
          {recipesList?.length > 0 && (
            <Row className="mt-3 w-100 d-flex justify-content-between align-items-center">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted">
                  {/* Showing {recipesList.length} recipe
                  {recipesList.length !== 1 ? "s" : ""} on page {pageNumber} */}
                </div>
                <div>
                  <button
                    className="btn btn-outline-primary btn-sm me-2"
                    disabled={pageNumber === 1 || isloading}
                    onClick={handlePreviousPage}
                  >
                    Prev
                  </button>
                  {/* <span className="me-2 ms-2 text-muted">
                    Page {pageNumber}
                  </span> */}
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={!hasMore || isloading}
                    onClick={handleNextPage}
                  >
                    Next
                  </button>
                </div>
              </div>
            </Row>
          )}
        </div>
      )}
      <RecipeDrawer
        recipeId={recipeDetails?.id}
        visible={visible}
        onClose={() => closeDrawer()}
      />
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default Recipes;
